import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Lock, Download, Trash2, Users, Mail, MailX, ArrowLeft, Loader2, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Lock strokeWidth={1} size={32} className="text-primary mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-light tracking-tight font-['Cormorant_Garamond']">
            Admin Access
          </h1>
          <p className="text-sm font-['Outfit'] text-muted-foreground mt-2">
            Enter the admin password to view registrations
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            type="password"
            data-testid="admin-password-input"
            placeholder="Password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            className="rounded-none bg-transparent border-0 border-b border-white/20 focus:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-12 text-base font-['Outfit'] font-light placeholder:text-muted-foreground/50"
          />
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400 font-['Outfit']">{error}</p>}
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.2em] text-xs h-12"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {loading ? "Authenticating..." : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token }) {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const headers = { "x-admin-token": token };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [regsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setRegistrations(regsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    const url = `${API}/admin/registrations/export`;
    const link = document.createElement("a");
    // Use fetch with header for auth
    fetch(url, { headers })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = "registrations.csv";
        link.click();
        URL.revokeObjectURL(blobUrl);
      });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this registration?")) return;
    try {
      await axios.delete(`${API}/admin/registrations/${id}`, { headers });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = registrations.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.full_name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.phone.includes(q)
    );
  });

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-link" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft strokeWidth={1} size={20} />
            </a>
            <h1 className="text-lg font-['Cormorant_Garamond'] font-light tracking-tight">
              Event Registrations
            </h1>
          </div>
          <Button
            data-testid="export-csv-btn"
            onClick={handleExport}
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.2em] text-xs h-10 px-6"
          >
            <Download strokeWidth={1} size={14} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-card/50 border border-white/5 p-6" data-testid="stat-total">
              <Users strokeWidth={1} size={20} className="text-primary mb-3" />
              <p className="text-3xl font-light tracking-tight">{stats.total_registrations}</p>
              <p className="text-xs font-['Outfit'] text-muted-foreground tracking-wide uppercase mt-1">Total Registrations</p>
            </div>
            <div className="bg-card/50 border border-white/5 p-6" data-testid="stat-emails-sent">
              <Mail strokeWidth={1} size={20} className="text-primary mb-3" />
              <p className="text-3xl font-light tracking-tight">{stats.emails_sent}</p>
              <p className="text-xs font-['Outfit'] text-muted-foreground tracking-wide uppercase mt-1">Emails Sent</p>
            </div>
            <div className="bg-card/50 border border-white/5 p-6" data-testid="stat-email-config">
              {stats.email_configured ? (
                <Mail strokeWidth={1} size={20} className="text-green-400 mb-3" />
              ) : (
                <MailX strokeWidth={1} size={20} className="text-muted-foreground mb-3" />
              )}
              <p className="text-sm font-['Outfit'] font-light mt-1">
                {stats.email_configured ? "Email Active" : "Email Not Configured"}
              </p>
              <p className="text-xs font-['Outfit'] text-muted-foreground mt-1">
                {stats.email_configured ? "Confirmation emails are being sent" : "Set EMAIL_PROVIDER & EMAIL_API_KEY in .env"}
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search strokeWidth={1} size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="admin-search-input"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none bg-transparent border-0 border-b border-white/20 focus:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 pl-7 h-12 text-base font-['Outfit'] font-light placeholder:text-muted-foreground/50"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-primary" size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground font-['Outfit']">
              {search ? "No registrations match your search." : "No registrations yet."}
            </p>
          </div>
        ) : (
          <div className="border border-white/5 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs font-['Outfit'] font-semibold tracking-[0.1em] uppercase text-muted-foreground">Name</TableHead>
                  <TableHead className="text-xs font-['Outfit'] font-semibold tracking-[0.1em] uppercase text-muted-foreground">Email</TableHead>
                  <TableHead className="text-xs font-['Outfit'] font-semibold tracking-[0.1em] uppercase text-muted-foreground">Phone</TableHead>
                  <TableHead className="text-xs font-['Outfit'] font-semibold tracking-[0.1em] uppercase text-muted-foreground">Registered</TableHead>
                  <TableHead className="text-xs font-['Outfit'] font-semibold tracking-[0.1em] uppercase text-muted-foreground w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((reg) => (
                  <TableRow key={reg.id} className="border-white/5" data-testid={`reg-row-${reg.id}`}>
                    <TableCell className="font-['Outfit'] font-light">{reg.full_name}</TableCell>
                    <TableCell className="font-['Outfit'] font-light text-muted-foreground">{reg.email}</TableCell>
                    <TableCell className="font-['Outfit'] font-light text-muted-foreground">{reg.phone}</TableCell>
                    <TableCell className="font-['Outfit'] font-light text-muted-foreground text-sm">{formatDate(reg.registered_at)}</TableCell>
                    <TableCell>
                      <button
                        data-testid={`delete-reg-${reg.id}`}
                        onClick={() => handleDelete(reg.id)}
                        className="text-muted-foreground hover:text-red-400 transition-colors"
                      >
                        <Trash2 strokeWidth={1} size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <p className="text-xs font-['Outfit'] text-muted-foreground/50 mt-6 text-center">
          Showing {filtered.length} of {registrations.length} registrations
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(null);

  return token ? <Dashboard token={token} /> : <LoginForm onLogin={setToken} />;
}
